import React, { useEffect, useState, useCallback, useRef, useMemo } from "react";
import axios from "axios";
import { useParams, Link } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import { Toaster } from "sonner";
import { Loader2, ArrowLeft } from "lucide-react";
import Navbar from "@/components/Navbar";
import Hero from "@/components/Hero";
import TourGrid from "@/components/TourGrid";
import BookSection from "@/components/BookSection";
import Viewer360 from "@/components/Viewer360";
import FlatVideoPlayer from "@/components/FlatVideoPlayer";
import FlatImageViewer from "@/components/FlatImageViewer";
import WidePanoViewer from "@/components/WidePanoViewer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PropertyPage = () => {
  const { slug } = useParams();
  const [property, setProperty] = useState(null);
  const [tours, setTours] = useState([]);
  const [activeTour, setActiveTour] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [soundOn, setSoundOn] = useState(true);
  const audioRef = useRef(null);

  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    Promise.all([
      axios.get(`${API}/properties/${slug}`),
      axios.get(`${API}/properties/${slug}/tours`),
    ])
      .then(([p, t]) => {
        setProperty(p.data);
        setTours(t.data);
      })
      .catch(() => {
        setNotFound(true);
      })
      .finally(() => setLoading(false));
  }, [slug]);

  const heroVideoTour = useMemo(
    () =>
      property?.video_url
        ? { id: "hero-video", title: "Video Tour", room: "Walk-through", media_type: "video", video_url: property.video_url }
        : null,
    [property]
  );

  const openHeroTour = useCallback(() => {
    if (heroVideoTour) setActiveTour(heroVideoTour);
    else if (tours.length) setActiveTour(tours[0]);
  }, [heroVideoTour, tours]);

  const scrollToBook = useCallback(() => {
    document.getElementById("enquire")?.scrollIntoView({ behavior: "smooth" });
  }, []);

  useEffect(() => {
    document.body.style.overflow = activeTour ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [activeTour]);

  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    if (activeTour && soundOn) {
      a.volume = 0.35;
      a.play().catch(() => {});
    } else {
      a.pause();
    }
  }, [activeTour, soundOn]);

  const toggleSound = useCallback(() => setSoundOn((s) => !s), []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#04271B]">
        <Loader2 className="w-8 h-8 text-[#D6B15E] animate-spin" strokeWidth={1.5} />
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-[#04271B] text-center px-6">
        <p className="font-heading text-4xl mb-4">Property not found</p>
        <Link to="/" className="text-[#D6B15E] tracking-widest uppercase text-sm inline-flex items-center gap-2">
          <ArrowLeft className="w-4 h-4" strokeWidth={1.5} /> Back to all properties
        </Link>
      </div>
    );
  }

  const viewerTours = heroVideoTour ? [heroVideoTour, ...tours] : tours;

  return (
    <div data-testid="property-page">
      <Toaster theme="dark" position="top-center" />
      <Navbar onBook={scrollToBook} />

      <div className="fixed top-[68px] left-0 right-0 z-30 pointer-events-none">
        <div className="max-w-6xl mx-auto px-6 sm:px-10 pt-4">
          <Link
            to="/"
            data-testid="back-to-properties"
            className="pointer-events-auto inline-flex items-center gap-2 backdrop-blur-2xl bg-black/40 border border-white/10 rounded-full px-4 py-2 text-xs tracking-widest uppercase text-white hover:bg-white/10 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" strokeWidth={1.5} /> All properties
          </Link>
        </div>
      </div>

      <Hero property={property} onExplore={openHeroTour} />
      <TourGrid property={property} tours={tours} onOpenTour={setActiveTour} />
      <BookSection property={property} />

      <AnimatePresence>
        {activeTour && (() => {
          const t = activeTour;
          const common = {
            tour: t,
            tours: viewerTours,
            onClose: () => setActiveTour(null),
            onSelectTour: setActiveTour,
            soundOn,
            onToggleSound: toggleSound,
          };
          if (t.media_type === "pano_photo" || t.media_type === "pano_video") return <Viewer360 {...common} />;
          if (t.media_type === "pano_wide") return <WidePanoViewer {...common} />;
          if (t.media_type === "photo") return <FlatImageViewer {...common} />;
          return <FlatVideoPlayer {...common} />;
        })()}
      </AnimatePresence>

      <audio ref={audioRef} src="/audio/ambient.mp3" loop preload="auto" data-testid="ambient-audio" />
    </div>
  );
};

export default PropertyPage;
